import { calculatePrizePoolRange, calculateDurationDays } from "./selfRegisterValidation";
import type { SelfRegisterFormData } from "@/store/selfRegisterFormStore";

export type SelfRegisterTrackingData = {
  // Contact info
  company_name: string;
  telegram_handle: string;
  email: string;
  // Campaign info
  project_name: string;
  token_pair: string;
  exchanges_selected: string[];
  exchanges_count: number;
  chains_selected: string[];
  chains_count: number;
  // Expectations
  expected_daily_volume: string;
  campaign_start_date: string;
  campaign_end_date: string;
  campaign_duration_days: number | null;
  // Discovery
  discovery_source: string;
  discovery_source_raw: string;
  // Optional
  note: string | null;
  has_note: boolean;
  funding_acknowledged: boolean;
  // Calculated values
  prize_pool_low: number | null;
  prize_pool_high: number | null;
  total_expected_volume: number | null;
};

/**
 * Converts form data to a standardized tracking payload.
 * This ensures consistent property names and includes all relevant data
 * for analytics across all self-register campaign events.
 */
export const getFormTrackingData = (data: SelfRegisterFormData): SelfRegisterTrackingData => {
  const prizePoolRange = calculatePrizePoolRange(data);
  const durationDays = calculateDurationDays(data.campaignStartDate, data.campaignEndDate);

  return {
    // Contact info
    company_name: data.companyName,
    telegram_handle: data.telegramHandle,
    email: data.email,
    // Campaign info
    project_name: data.projectName,
    token_pair: data.tokenPair,
    exchanges_selected: data.exchanges,
    exchanges_count: data.exchanges.length,
    chains_selected: data.chains,
    chains_count: data.chains.length,
    // Expectations
    expected_daily_volume: data.expectedDailyVolume,
    campaign_start_date: data.campaignStartDate,
    campaign_end_date: data.campaignEndDate,
    campaign_duration_days: durationDays,
    // Discovery - use custom source if "other" is selected, otherwise use selected option
    discovery_source:
      data.discoverySource === "other" && data.discoverySourceOther?.trim()
        ? data.discoverySourceOther.trim()
        : data.discoverySource,
    discovery_source_raw: data.discoverySource,
    // Optional
    note: data.note?.trim() || null,
    has_note: Boolean(data.note?.trim()),
    funding_acknowledged: data.fundingAcknowledged,
    // Calculated values
    prize_pool_low: prizePoolRange?.low ?? null,
    prize_pool_high: prizePoolRange?.high ?? null,
    total_expected_volume: prizePoolRange?.totalVolume ?? null,
  };
};

/**
 * Get tracking data for a specific form step.
 * Returns only the fields relevant to that step plus any previously entered data.
 */
export const getStepTrackingData = (
  data: SelfRegisterFormData,
  completedStepId: "contact" | "campaign" | "expectations",
) => {
  const fullData = getFormTrackingData(data);

  // Always include step info
  const baseData = {
    completed_step: completedStepId,
  };

  switch (completedStepId) {
    case "contact":
      return {
        ...baseData,
        company_name: fullData.company_name,
        telegram_handle: fullData.telegram_handle,
        email: fullData.email,
      };
    case "campaign":
      return {
        ...baseData,
        // Include previous step data
        company_name: fullData.company_name,
        telegram_handle: fullData.telegram_handle,
        email: fullData.email,
        // Current step data
        project_name: fullData.project_name,
        token_pair: fullData.token_pair,
        exchanges_selected: fullData.exchanges_selected,
        exchanges_count: fullData.exchanges_count,
        chains_selected: fullData.chains_selected,
        chains_count: fullData.chains_count,
      };
    case "expectations":
      // Last step - return all data
      return {
        ...baseData,
        ...fullData,
      };
    default:
      return baseData;
  }
};
